import { type NitroFetchOptions } from 'nitropack';
import { APIImplementation } from '~/utils/requestsProcessor/implementation';
import { type TRequestsHandler } from '~/utils/requestsProcessor/types';

type InjectedCallback = () => Promise<boolean | null | void> | void | null | boolean;
type InjectedErrorCallback = (code: number | string, status?: string) => Promise<void> | void;
type InjectedAfterCallback = <T = unknown>(response: T) => Promise<boolean | null | void> | void | null | boolean;

// TODO: add refresh lock mechanism
// TODO: add callbacks usage implementation

export default class RequestsProcessor {
  #lock = false;
  #refreshMechanismImplementation?: () => Promise<boolean>;
  #requestsHandlerImplementation?: TRequestsHandler;
  readonly #apiImplementation: APIImplementation;

  #beforeRequestCallbacks: InjectedCallback[] = [];
  #afterRequestCallbacks: InjectedAfterCallback[] = [];
  #onErrorCallbacks: InjectedErrorCallback[] = [];

  constructor(private readonly baseUrl: string) {
    this.#apiImplementation = new APIImplementation(this.makeRequest.bind(this));
  }

  public registerRefreshMechanism(callback: () => Promise<boolean>) {
    if (this.#refreshMechanismImplementation) throw new Error('Refresh mechanism already registered');
    this.#refreshMechanismImplementation = callback;
  }

  public registerRequestsHandler(callback: (url: string, options: NitroFetchOptions<any>) => Promise<any>) {
    if (this.#requestsHandlerImplementation) throw new Error('Requests handler already registered');
    this.#requestsHandlerImplementation = callback;
  }

  public get api() {
    return this.#apiImplementation;
  }

  public addBeforeRequestCallback(callback: InjectedCallback) {
    this.#beforeRequestCallbacks.push(callback);
    return this;
  }

  public addAfterRequestCallback(callback: InjectedAfterCallback) {
    this.#afterRequestCallbacks.push(callback);
    return this;
  }

  public addOnErrorCallback(callback: InjectedErrorCallback) {
    this.#onErrorCallbacks.push(callback);
    return this;
  }

  public removeCallback(callback: Function) {
    this.#beforeRequestCallbacks = this.#beforeRequestCallbacks.filter((cb) => cb !== callback);
    this.#afterRequestCallbacks = this.#afterRequestCallbacks.filter((cb) => cb !== callback);
    this.#onErrorCallbacks = this.#onErrorCallbacks.filter((cb) => cb !== callback);
  }

  private async waitForUnlock() {
    if (!this.#lock) return;
    while (this.#lock) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  private async makeRequest<T = unknown>(path: string, options: NitroFetchOptions<any>): Promise<T> {
    await this.waitForUnlock();

    const url = `${this.baseUrl}/${path}`.replace(/\/+/g, '/');
    if (!this.#requestsHandlerImplementation) throw new Error('Requests handler not registered');

    for (const callback of this.#beforeRequestCallbacks) {
      const result = await callback();
      if (result === false) throw new Error('Request aborted with beforeCallback');
    }

    try {
      const result = await this.#requestsHandlerImplementation(url, options).catch(
        async (code: any, status?: string) => {
          for (const callback of this.#onErrorCallbacks) {
            await callback(typeof code === 'number' ? code : String(code?.code ?? code), status);
          }

          return Error(`Request failed with code: ${code} and status: ${status}`);
        }
      );

      if (result instanceof Error) throw result;

      for (const callback of this.#afterRequestCallbacks) {
        const res = await callback(result);
        if (res === false) throw new Error('Request aborted with afterCallback');
      }

      return result as T;
    } catch (err) {
      this.#lock = true;
      if (this.#refreshMechanismImplementation) {
        const refreshed = await this.#refreshMechanismImplementation().finally(() => (this.#lock = false));
        if (refreshed) return this.#requestsHandlerImplementation(url, options);
      }

      this.#lock = false;
      throw err;
    }
  }
}
